<template>
  <AuthButton>
    <div class="relative shrink-0">
      <img
        v-if="authUser?.photoURL"
        :src="authUser?.photoURL"
        alt="user"
        class="w-11 h-11 rounded-full"
      />
      <img
        v-else
        src="../../assets/avatar.svg"
        alt="user"
        class="w-11 h-11 rounded-full"
      />
      <div v-if="isSubscriber" class="absolute -right-0.5 -bottom-0.5">
        <img
          v-if="
            subscriberType == 'launch-pro-monthly' ||
            subscriberType == 'launch-pro-yearly' ||
            subscriberType == 'pro-monthly' ||
            subscriberType == 'pro-yearly'
          "
          class="w-4"
          :src="PremiumBadge"
          alt="pro badge"
        />
        <img
          v-if="
            subscriberType == 'max-monthly' || subscriberType == 'max-yearly'
          "
          class="w-4"
          :src="badgeMax"
          alt="max badge"
        />
        <img
          v-if="subscriberType == 'pro-lifetime'"
          class="w-4"
          :src="badgeLifetime"
          alt="lifetime badge"
        />
      </div>
    </div>
  </AuthButton>

  <div v-if="authUser">
    <Menu as="div" class="relative">
      <div class="flex items-center self-start">
        <MenuButton
          class="inline-flex w-full justify-center rounded-md text-label-lg text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75"
          @click="closeModal"
        >
          <UseAuthButton />
        </MenuButton>
      </div>
      <transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-out"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
        <MenuItems
          class="absolute right-0 bg-white top-full mt-1 rounded-md shadow-md min-w-[141px] pb-3.5 z-[999] max-w-44"
        >
          <div class="flex flex-col gap-y-5">
            <MenuItem>
              <div
                class="text-muted-foreground text-label-lg border-b py-1 max-w-44 px-3.5 truncate"
              >
                {{ authUser?.email }}
              </div>
            </MenuItem>
            <MenuItem v-if="isSubscriber">
              <div
                class="flex gap-x-2 grow cursor-pointer px-3.5"
                @click="manageSubscription"
              >
                <img src="../../assets/ticket.png" alt="" class="w-5" />
                <span class="whitespace-nowrap text-[#101828] text-label-lg">
                  {{ $t(`navbar.Subscription`, "Subscription") }}
                </span>
              </div>
            </MenuItem>
            <MenuItem>
              <div
                class="flex gap-x-2 grow cursor-pointer px-3.5"
                @click="logOut"
              >
                <img src="../../assets/logout.png" alt="" class="w-5" />
                <span class="whitespace-nowrap text-[#101828] text-label-lg">
                  {{ $t(`navbar.LogOut`, "Log Out") }}
                </span>
              </div>
            </MenuItem>
          </div>
        </MenuItems>
      </transition>
    </Menu>
  </div>
  <div v-else>
    <Button
      variant="highlight"
      class="px-[30px] py-3 rounded-lg h-max"
      @click="handleLogin"
    >
      <span class="text-label-lg">
        {{ $t(`navbar.Login`, "Login") }} /
        {{ $t(`navbar.SignUp`, "Sign Up") }}
      </span>
    </Button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import Button from "@/components/ui/button/Button.vue";
import { useMockupStore } from "@/datastore/module/mockup.js";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { useRoute, useRouter } from "vue-router";
import { getAuth, signOut } from "firebase/auth";
import PremiumBadge from "@/assets/premiumBadge.svg";
import badgeMax from "@/assets/MaxBadge.jpg";
import badgeLifetime from "@/assets/LifetimeBadge.svg";
import SubscriptionServices from "@/services/subscription.js";
import { createReusableTemplate } from "@vueuse/core";

const store = useMockupStore();
const route = useRoute();
const router = useRouter();
const [AuthButton, UseAuthButton] = createReusableTemplate();

const emits = defineEmits(["logoutEvent"]);

const isModalOpen = ref(false);

const authUser = computed(() => store?.authUser);
const subscriberType = computed(() => store?.subscriberType);
const isSubscriber = computed(() => store?.isSubscriber);
const subscriptionId = computed(() => store?.subscriptionId);

const handleLogin = () => {
  localStorage.setItem("previousPage", window.location.href);
  router.push({ name: "login" });
};

const closeModal = () => {
  isModalOpen.value = !isModalOpen.value;
};

const logOut = async () => {
  const auth = getAuth();
  try {
    isModalOpen.value = false;
    await signOut(auth);
    store.resetLoadingState();
    if (route.name === "mockup-editor") {
      emits("logoutEvent", route.params.id);
    } else {
      router.go(0);
    }
  } catch (error) {
    console.error("Sign out error:", error);
  }
};

const manageSubscription = async () => {
  const response = await SubscriptionServices.getSubscription(
    subscriptionId.value,
    authUser.value.email,
  );
  const data = await response.json();
  if (data?.url) {
    window.open(data.url, "_blank");
  } else {
    console.error(response);
  }
};
</script>
